import { landingGroups } from './landingData';
import { Link, NavLink } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../lib/auth';
import { useCustomer } from '../lib/customer';
import { supabase } from '../lib/supabase';
import { theme } from '../styles/theme';
import { Logo } from './Logo';
import { SearchAutocomplete } from './SearchAutocomplete';
import { CurrencyToggle } from './Price';
import { VisualNavigation } from './VisualNavigation';
import { versionedImageUrl } from '../lib/imageUrl';

// Nav tree con sub-categorías. Las sub-categorías pasan filtros al catálogo.
type NavItem = {
  label: string;
  to: string;
  subs?: { label: string; to: string; divider?: boolean }[];
  eyebrow?: string;
  isLutron?: boolean;
};

// Lutron es manual (no en DB con category_paths). Las NULED se cargan dinámicas.
const LUTRON_NAV: NavItem = {
  label: 'LUTRON',
  to: '/lutron',
  eyebrow: 'Marca representada',
  isLutron: true,
  subs: [
    { label: 'Caséta',                       to: '/catalogo?brand=lutron&line=caseta' },
    { label: 'RadioRA 3',                    to: '/catalogo?brand=lutron&line=radiora3' },
    { label: 'Maestro',                      to: '/catalogo?brand=lutron&line=maestro' },
    { label: 'Wallbox',                      to: '/catalogo?brand=lutron&line=wallbox' },
    { label: 'Vive',                         to: '/catalogo?brand=lutron&line=vive' },
    { label: 'Sunnata',                      to: '/catalogo?brand=lutron&line=sunnata' },
    { label: 'HomeWorks · solo certificados', to: '/lutron/homeworks', divider: true },
    { label: '★ Sobre la línea Lutron',      to: '/lutron' },
    { label: 'Asesoría guiada · 2 min',      to: '/lutron/asesoria' },
  ],
};

const STATIC_NAV: NavItem[] = [
  { label: 'COMPRAR TODOS', to: '/catalogo' },
  { label: 'COMPOSE', to: '/compose', eyebrow: 'Sistema modular' },
  { label: 'ARMA TU RIEL', to: '/arma-tu-riel', eyebrow: 'Configurador magnético' },
];

export function Header({ visualNav = true, variant = 'default' }: { visualNav?: boolean; variant?: 'default' | 'compose' }) {
  const { session, staff, signOut } = useAuth();
  const { customer } = useCustomer();
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [navTree, setNavTree] = useState<NavItem[]>(STATIC_NAV.concat([LUTRON_NAV]));
  const navRef = useRef<HTMLDivElement | null>(null);
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const isMobile = useIsMobile();

  // Construye mega-menú desde category_paths reales
  useEffect(() => {
    supabase.rpc('nuled_category_tree').then(({ data }) => {
      const rows = (data as { path: string; depth: number; count: number }[] | null) ?? [];
      const tops = rows.filter(r => r.depth === 1 && r.path !== 'COMPOSE');
      const dynamicItems: NavItem[] = tops.map(top => {
        const subs = rows
          .filter(r => r.depth === 2 && r.path.startsWith(top.path + '/'))
          .map(r => ({
            label: `${r.path.split('/').pop()} (${r.count})`,
            to: `/catalogo?path=${encodeURIComponent(r.path)}`,
          }));
        return {
          label: top.path.toUpperCase(),
          to: `/catalogo?path=${encodeURIComponent(top.path)}`,
          subs: subs.length > 0 ? subs : undefined,
        };
      });
      setNavTree([...STATIC_NAV, ...dynamicItems, LUTRON_NAV]);
    });
  }, []);

  // Click outside cierra dropdown
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) setOpenIdx(null);
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) setUserMenuOpen(false);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  return (
    <div className={`site-header-shell${variant === 'compose' ? ' site-header-shell--compose' : ''}`}>
      {/* Top promo strip */}
      <div className="site-promo-strip" style={{
        background: theme.bg,
        color: theme.text,
        textAlign: 'center',
        padding: '8px 16px',
        fontSize: 13,
        borderBottom: `1px solid ${theme.border}`,
      }}>
        ¡Distribuidor mayorista <strong>autorizado Lutron</strong> en México!
      </div>

      {!isMobile && (<>
      {/* Main header */}
      <header className="site-main-header" style={{
        background: theme.brand,
        color: theme.textOnBrand,
      }}>
        <div className="site-main-header__inner" style={{
          maxWidth: theme.maxWidth,
          margin: '0 auto',
          padding: '20px 24px',
          display: 'grid',
          gridTemplateColumns: 'auto 1fr auto',
          alignItems: 'center',
          gap: 32,
        }}>
          <Link to="/" style={{ color: theme.textOnBrand, display: 'flex', alignItems: 'center', gap: 12 }}>
            <Logo height={50} />
          </Link>

          <SearchAutocomplete />

          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <CurrencyToggle size="sm" />
            {!session && !staff?.active && (
              <Link to="/distribuidor" style={pillBtn}>Vuélvete distribuidor</Link>
            )}
            {session ? (
              <div ref={userMenuRef} style={{ position: 'relative' }}>
                <button
                  onClick={() => setUserMenuOpen(v => !v)}
                  style={{
                    ...iconAction,
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    fontFamily: theme.font.sans,
                  }}
                >
                  <UserIcon />
                  <span style={{ marginLeft: 8 }}>
                    {staff?.active ? 'Admin' : (customer?.business_name?.split(' ')[0] ?? 'Mi cuenta')}
                  </span>
                  <Chevron open={userMenuOpen} />
                </button>
                {userMenuOpen && (
                  <div
                    onClick={() => setUserMenuOpen(false)}
                    style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      marginTop: 8,
                      minWidth: 220,
                      background: theme.bg,
                      border: `1px solid ${theme.border}`,
                      boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
                      zIndex: 60,
                      padding: '6px 0',
                    }}
                  >
                    {staff?.active && (
                      <UserMenuLink to="/admin">⚙ Backoffice / Admin</UserMenuLink>
                    )}
                    {customer && (
                      <>
                        <UserMenuLink to="/portal">📊 Mi panel</UserMenuLink>
                        <UserMenuLink to="/portal?tab=cotizaciones">📝 Mis cotizaciones</UserMenuLink>
                        <UserMenuLink to="/portal?tab=precios">🏷 Mis precios</UserMenuLink>
                      </>
                    )}
                    <UserMenuLink to="/catalogo">📦 Seguir comprando</UserMenuLink>
                    <div style={{ height: 1, background: theme.border, margin: '6px 0' }} />
                    <button
                      onClick={async () => { await signOut(); setUserMenuOpen(false); }}
                      style={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        padding: '10px 16px',
                        background: 'transparent',
                        border: 'none',
                        color: theme.err,
                        fontSize: 13,
                        fontWeight: 500,
                        cursor: 'pointer',
                        fontFamily: theme.font.sans,
                      }}
                    >
                      Cerrar sesión
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login" style={iconAction}>
                <UserIcon />
                <span style={{ marginLeft: 8 }}>Inicia Sesión</span>
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Sub-nav con dropdowns */}
      {visualNav ? <VisualNavigation /> : <nav
        ref={navRef}
        style={{
          background: theme.bg,
          borderBottom: `1px solid ${theme.border}`,
          position: 'relative',
        }}
        onMouseLeave={() => setOpenIdx(null)}
      >
        <div style={{
          maxWidth: theme.maxWidth,
          margin: '0 auto',
          padding: '0 24px',
          display: 'flex',
          alignItems: 'stretch',
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}>
          {navTree.map((item, idx) => {
            const isOpen = openIdx === idx;
            const hasSubs = !!item.subs?.length;
            const isLutron = item.isLutron;
            return (
              <div
                key={item.label}
                onMouseEnter={() => hasSubs && setOpenIdx(idx)}
                style={{ position: 'relative' }}
              >
                {/* Lutron item — separador antes */}
                {isLutron && (
                  <span aria-hidden="true" style={{
                    position: 'absolute',
                    left: -1,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 1,
                    height: 18,
                    background: theme.border,
                  }} />
                )}

                <NavLink
                  to={item.to}
                  end={item.to === '/catalogo'}
                  onClick={() => setOpenIdx(null)}
                  style={({ isActive }) => ({
                    padding: isLutron ? '12px 14px 13px' : '16px 12px',
                    display: 'flex',
                    flexDirection: isLutron ? 'column' : 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: isActive || isOpen ? theme.brand : theme.text,
                    fontSize: 12.5,
                    fontWeight: 700,
                    letterSpacing: '0.4px',
                    textTransform: 'uppercase',
                    textDecoration: 'none',
                    borderBottom: isActive || isOpen
                      ? `3px solid ${theme.accent}`
                      : '3px solid transparent',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.1,
                    cursor: 'pointer',
                  })}
                >
                  {isLutron && (
                    <span style={{
                      fontSize: 9,
                      letterSpacing: 1.5,
                      color: theme.accentDark,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      marginBottom: 2,
                    }}>
                      {item.eyebrow}
                    </span>
                  )}
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {item.label}
                    {hasSubs && (
                      <Chevron open={isOpen} />
                    )}
                  </span>
                </NavLink>

                {/* Dropdown */}
                {isOpen && hasSubs && (
                  <div
                    onClick={() => setOpenIdx(null)}
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      minWidth: 260,
                      background: theme.bg,
                      border: `1px solid ${theme.border}`,
                      borderTop: 'none',
                      boxShadow: '0 8px 20px rgba(0,0,0,0.08)',
                      zIndex: 50,
                      padding: '8px 0',
                    }}
                  >
                    {item.subs!.map((sub, i) => (
                      <div key={sub.label}>
                        {sub.divider && i > 0 && (
                          <div style={{ height: 1, background: theme.border, margin: '6px 12px' }} />
                        )}
                        <Link
                          to={sub.to}
                          style={{
                            display: 'block',
                            padding: '10px 16px',
                            color: theme.brand,
                            textDecoration: 'none',
                            fontSize: 13,
                            fontWeight: 500,
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.style.background = theme.bgSoft;
                            e.currentTarget.style.color = theme.accentDark;
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.color = theme.brand;
                          }}
                        >
                          {sub.label}
                        </Link>
                      </div>
                    ))}
                    {/* Footer del dropdown — link al filtro principal */}
                    <div style={{
                      borderTop: `1px solid ${theme.border}`,
                      padding: '10px 16px',
                      marginTop: 6,
                    }}>
                      <Link
                        to={item.to}
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: theme.accentDark,
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          textDecoration: 'none',
                        }}
                      >
                        Ver todo {isLutron ? 'Lutron' : item.label.toLowerCase()} →
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </nav>}
      </>)}

      {isMobile && (
        <MobileHeader navTree={navTree} session={session} staff={staff} customer={customer} signOut={signOut} />
      )}
    </div>
  );
}

// ---- Móvil ----
function useIsMobile(bp = 860): boolean {
  const [m, setM] = useState<boolean>(typeof window !== 'undefined' ? window.innerWidth <= bp : false);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${bp}px)`);
    const on = () => setM(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [bp]);
  return m;
}

function MobileHeader({ navTree, session, staff, customer, signOut }: {
  navTree: NavItem[];
  session: unknown;
  staff: { active?: boolean } | null | undefined;
  customer: { business_name?: string } | null | undefined;
  signOut: () => Promise<void> | void;
}) {
  const [open, setOpen] = useState(false);
  const [exp, setExp] = useState<number | null>(null);
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  return (
    <>
      <header className="site-mobile-header" style={{ background: theme.brand, color: theme.textOnBrand, position: 'sticky', top: 0, zIndex: 40 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px' }}>
          <button onClick={() => setOpen(true)} aria-label="Menú" style={{ background: 'transparent', border: 'none', color: theme.textOnBrand, padding: 4, cursor: 'pointer', display: 'flex' }}>
            <BurgerIcon />
          </button>
          <Link to="/" style={{ color: theme.textOnBrand, display: 'flex', alignItems: 'center', flex: 1 }}>
            <Logo height={33} />
          </Link>
          <CurrencyToggle size="sm" />
          <Link to={session ? (staff?.active ? '/admin' : '/portal') : '/login'} style={{ color: theme.textOnBrand, display: 'flex', padding: 4 }} aria-label="Cuenta">
            <UserIcon />
          </Link>
        </div>
        <div style={{ padding: '0 14px 12px' }}>
          <SearchAutocomplete />
        </div>
      </header>

      {open && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 100 }}>
          <div onClick={() => setOpen(false)} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)' }} />
          <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: '86%', maxWidth: 360, background: theme.bg, boxShadow: '0 0 40px rgba(0,0,0,0.35)', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', background: theme.brand }}>
              <Logo height={33} />
              <button onClick={() => setOpen(false)} aria-label="Cerrar" style={{ background: 'transparent', border: 'none', color: theme.textOnBrand, fontSize: 26, lineHeight: 1, cursor: 'pointer', padding: 0 }}>×</button>
            </div>

            <nav style={{ flex: 1 }}>
              {navTree.map((item, idx) => {
                const hasSubs = !!item.subs?.length;
                const isExp = exp === idx;
                const categoryPhoto = landingGroups.find(g => g.name.toLowerCase() === item.label.toLowerCase())?.slides[0]?.url;
                return (
                  <div key={item.label} style={{ borderBottom: `1px solid ${theme.border}` }}>
                    <div style={{ display: 'flex', alignItems: 'stretch' }}>
                      <Link to={item.to} onClick={() => setOpen(false)} style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', color: theme.brand, fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.4, textDecoration: 'none' }}>
                        {categoryPhoto && <img src={versionedImageUrl(categoryPhoto)} alt="" loading="lazy" style={{ width: 48, height: 48, objectFit: 'contain', background: 'white', flexShrink: 0 }} />}
                        <span>{item.eyebrow && <div style={{ fontSize: 9, color: theme.accentDark, letterSpacing: 1.2, marginBottom: 2 }}>{item.eyebrow}</div>}
                        {item.label}</span>
                      </Link>
                      {hasSubs && (
                        <button onClick={() => setExp(isExp ? null : idx)} aria-label="Expandir" style={{ background: 'transparent', border: 'none', borderLeft: `1px solid ${theme.border}`, padding: '0 18px', color: theme.brand, cursor: 'pointer' }}>
                          <Chevron open={isExp} />
                        </button>
                      )}
                    </div>
                    {hasSubs && isExp && (
                      <div style={{ background: theme.bgSoft, paddingBottom: 6 }}>
                        {item.subs!.map(sub => (
                          <Link key={sub.label} to={sub.to} onClick={() => setOpen(false)} style={{ display: 'block', padding: '10px 16px 10px 26px', color: theme.brand, fontSize: 13, textDecoration: 'none' }}>
                            {sub.label}
                          </Link>
                        ))}
                        <Link to={item.to} onClick={() => setOpen(false)} style={{ display: 'block', padding: '8px 16px 8px 26px', color: theme.accentDark, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, textDecoration: 'none' }}>
                          Ver todo →
                        </Link>
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>

            <div style={{ padding: 16, borderTop: `1px solid ${theme.border}`, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {session ? (
                <>
                  {staff?.active && <Link to="/admin" onClick={() => setOpen(false)} style={drawerLink}>⚙ Backoffice / Admin</Link>}
                  {customer && (
                    <>
                      <Link to="/portal" onClick={() => setOpen(false)} style={drawerLink}>📊 Mi panel</Link>
                      <Link to="/portal?tab=cotizaciones" onClick={() => setOpen(false)} style={drawerLink}>📝 Mis cotizaciones</Link>
                      <Link to="/portal?tab=precios" onClick={() => setOpen(false)} style={drawerLink}>🏷 Mis precios</Link>
                    </>
                  )}
                  <button onClick={async () => { await signOut(); setOpen(false); }} style={{ ...drawerLink, color: theme.err, background: 'transparent', border: 'none', textAlign: 'left', cursor: 'pointer' }}>Cerrar sesión</button>
                </>
              ) : (
                <>
                  <Link to="/distribuidor" onClick={() => setOpen(false)} style={{ border: `1px solid ${theme.brand}`, color: theme.brand, padding: '12px 16px', borderRadius: theme.radius.sm, fontSize: 13, fontWeight: 600, textDecoration: 'none', textAlign: 'center' }}>Vuélvete distribuidor</Link>
                  <Link to="/login" onClick={() => setOpen(false)} style={drawerLink}>Inicia sesión</Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const drawerLink: React.CSSProperties = {
  display: 'block', padding: '10px 4px', color: theme.brand, textDecoration: 'none', fontSize: 14, fontWeight: 500, fontFamily: theme.font.sans,
};

function BurgerIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  );
}

function UserMenuLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      style={{
        display: 'block',
        padding: '10px 16px',
        color: theme.brand,
        textDecoration: 'none',
        fontSize: 13,
        fontWeight: 500,
      }}
      onMouseEnter={e => { e.currentTarget.style.background = theme.bgSoft; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
    >
      {children}
    </Link>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      style={{
        transition: 'transform 0.15s',
        transform: open ? 'rotate(180deg)' : 'rotate(0)',
      }}
    >
      <path d="M2 3.5l3 3 3-3" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" />
    </svg>
  );
}

const pillBtn: React.CSSProperties = {
  border: `1px solid ${theme.textOnBrand}`,
  color: theme.textOnBrand,
  padding: '10px 16px',
  borderRadius: theme.radius.sm,
  fontSize: 13,
  fontWeight: 500,
  textDecoration: 'none',
  whiteSpace: 'nowrap',
};

const iconAction: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  color: theme.textOnBrand,
  textDecoration: 'none',
  fontSize: 13,
  whiteSpace: 'nowrap',
};

function UserIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4 3.6-7 8-7s8 3 8 7" strokeLinecap="round" />
    </svg>
  );
}
